/**
 * Sample Registration — domain model, lookup catalogs and pure helpers.
 *
 * Ported from the approved design prototype
 * `29131-AI_Sample Registration/Sample-Tracking-Module-Prototype.html`
 * (2026-07-18), which mirrors the legacy XAF view
 * `Samplecheckin_DetailView_Copy_SampleRegistration`.
 *
 * ── SCOPE OF THIS FILE (read before extending) ──────────────────────────────
 * The catalogs below (clients, projects, tests, matrices, TAT, storage, check
 * points, item charges …) are the prototype's REFERENCE DATA, kept here so the
 * design pass is fully interactive.  They are placeholders for real lookup
 * endpoints — every one is marked `TODO(AI-29131)` at its call site in
 * `useSrLookups`.  No business rule lives here that the API will not own:
 * bottle grouping / hold time / TAT→due date are reproduced from legacy so the
 * form behaves correctly on screen, and must be re-derived server-side when the
 * save endpoint lands (CLAUDE.md §1 "all business logic lives in the API").
 */

// ─────────────────────────────────────────────────────────────────────────────
// Model
// ─────────────────────────────────────────────────────────────────────────────

/** Per-bottle detail captured in the BottleID picker (one physical container). */
export interface SrBottleDetail {
  container: string;
  preserv: string;
  storage: string;
  ph: string;
  temp: string;
  paper: string;
  thermo: string;
}

/** Per-parameter (analyte) result row — only FIELD methods are keyed here; lab
 *  methods get their results later in Result Entry. */
export interface SrParamData {
  result: string;
  units: string;
  analysisDate: string;
  analyzedBy: string;
}

/** One test assignment on one sample (one sample → many tests). */
export interface SrTest {
  /** Stable client-side key — grids need an id that survives re-ordering. */
  key: string;
  name: string;
  method: string;
  /** Analytical component / variant ("Default", "Watershed", …). */
  comp: string;
  /** Parent group name for this test (empty string if none). */
  testGroupName: string;
  /** Assigned bottle letter(s), comma-joined ("A" or "A,B"). */
  bottle: string;
  container: string;
  preserv: string;
  storage: string;
  /** Per-bottle detail, lazily built by the bottle picker. */
  bd: Record<string, SrBottleDetail> | null;
  /** Analyte → included?  null = every analyte included (AllParam). */
  params: Record<string, boolean> | null;
  /** Analyte → field-method result row. */
  paramData: Record<string, SrParamData>;
  hdTime: string;
  status: string;
  remark: boolean;
  subOut: boolean;
  added: string;
  hold: boolean;
}

/** One sample (one row of the Sample Login grid). */
export interface SrSample {
  /** {JobID}-NNN — minted from the job id + ordinal. */
  id: string;
  hold: boolean;
  /** The wide Sample Login column set, keyed by SL_COLS name. */
  v: Record<string, string>;
  tests: SrTest[];
}

/** A receipt event — one Job ID can receive samples several times. */
export interface SrReceivedDate {
  key: string;
  /** Server Oid (dbo.SC_ReceivedDates.Oid); '' for a new, unsaved row. Round-
   *  trips so a later save UPDATEs the row instead of duplicating it. */
  oid: string;
  date: string;
  by: string;
  relinq: string;
  n: number;
  matrix: string[];
  tat: string;
  cooler: string;
  cat: string[];
  createdBy: string;
  created: string;
}

export interface SrAttachment {
  key: string;
  /** Server Oid (dbo.Attachment.Oid); '' for a new, unsaved row. */
  oid: string;
  name: string;
  cat: string;
  file: string;
  /** FileData FK Oid — links to dbo.FileData. */
  fileDataOid: string;
  date: string;
  op: string;
  /** Operator Employee Oid — for FK resolution on save. */
  opOid: string;
  recv: string;
  comment: string;
  /** Pending File object for upload — only present on NEW rows before save. */
  fileObj?: File;
}

/** Category int → display label (matches XAF Category enum). */
export const ATT_CAT_LABELS: Record<number, string> = {
  0: 'COC', 1: 'Photo', 2: 'Report', 3: 'ResultEDD', 4: 'Subout', 5: 'Other',
};

/** Display label → category int. */
export const ATT_CAT_VALUES: Record<string, number> = {
  COC: 0, Photo: 1, Report: 2, ResultEDD: 3, Subout: 4, Other: 5,
};

/** The subset of categories shown in the New/Edit dropdown. */
export const ATT_CAT_OPTIONS = ['COC', 'Report', 'Photo', 'Other'] as const;

/** Sample Condition Check — one per receipt event. */
export interface SrSccRecord {
  key: string;
  /** Server Oid (dbo.SampleConditionCheck.Oid); '' for a new, unsaved row. */
  oid: string;
  matrix: string;
  by: string;
  date: string;
  recv: string;
  temp: string;
  corr: string;
  thermo: string;
  ph: string;
  paper: string;
  /** Per-check-point answer, index-aligned to SCC_POINTS. */
  points: string[];
  /** Per-check-point description, index-aligned to SCC_POINTS. */
  descriptions: string[];
  comments: SrSccComment[];
}

export interface SrSccComment { text: string; user: string; dt: string }

export interface SrNote {
  key: string;
  /** Server Oid (dbo.Notes.Oid); '' for a new, unsaved row. */
  oid: string;
  title: string;
  author: string;
  /** The author's Employee Oid — used for author-only enforcement. */
  authorOid: string;
  date: string;
  sort: number;
  html: string;
  /** Plain-text version of the note (strip tags). */
  plainText: string;
  /** SourceID — links note to the job. */
  sourceId: string;
  /** NoteSource — e.g. "Sample Registration". */
  noteSource: string;
}

export interface SrItemCharge {
  key: string;
  /** Server Oid (dbo.SampleCheckinItemChargePricing.Oid); '' for a new, unsaved row. */
  oid: string;
  /** ItemChargePricing.Oid — sent as @itemPrice to the save SP so the FK is set correctly. */
  itemPriceOid: string;
  /** ItemChargePricing.ItemName — display value for the ItemName grid column. */
  name: string;
  /** Per-job freetext description entered by the user (SampleCheckinItemChargePricing.Description). */
  desc: string;
  price: number;
  qty: number;
  amount: number;
  final: number;
  /** Non-preferred unit price (NpUnitPrice). */
  npPrice: number;
  disc: number;
  remark: string;
}

export interface SrImage {
  key: string;
  /** Server Oid (dbo.Image.Oid); '' for a new, unsaved row. */
  oid: string;
  name: string;
  photo: string;
  sort: number;
  comment: string;
}

/** Every child collection the Registration bottom tabs own. */
export interface SrTabState {
  receivedDates: SrReceivedDate[];
  attachments: SrAttachment[];
  sccRecords: SrSccRecord[];
  notes: SrNote[];
  itemCharges: SrItemCharge[];
  images: SrImage[];
}

export function emptyTabState(): SrTabState {
  return { receivedDates: [], attachments: [], sccRecords: [], notes: [], itemCharges: [], images: [] };
}

/** Every value the Registration step's three field sections hold. */
export interface SrHeader {
  jobId: string;
  receivedDate: string;
  receivedBy: string;
  createdBy: string;
  client: string;
  /** The chosen client's exact Oid, captured at pick time. Drives the
   *  client-scoped Project / Collector / Contact / Quote lookups without
   *  ambiguity when two customers share a name. */
  clientOid: string;
  clientAddress: string;
  clientPhone: string;
  clientContact: string;
  quoteId: string;
  poNumber: string;
  paymentStatus: string;
  manifestNumber: string;
  projectManager: string;
  dateExpect: string;
  relinquishedBy: string[];
  collector: string[];
  dateCollected: string;
  timeCollected: string;
  projectId: string;
  projectName: string;
  projectLocation: string;
  permit: string;
  projectCategory: string;
  cocId: string;
  invoiceContact: string;
  registrationType: string;
  jobInactivation: string;
  cancelJob: boolean;
  prevStatusId: string;
  status: string;
  /** Sample Information — Sample Matrix is a CheckedComboBox (multi-select). */
  matrices: string[];
  /** Header-level test template (CheckedComboBox) — every generated sample
   *  starts from this set and is customised per sample in Test Assignment. */
  // Values are the XAF composite "TestName|MethodNumber" (e.g.
  // "Ammonia_Free|HACH 10200") so two same-named tests with different methods
  // are distinct + uniquely resolvable to their TestMethod Oid.
  testTemplate: string[];
  /** Composite "TestName|MethodNumber" → method, remembered from the picker rows. */
  testTemplateMethods: Record<string, string>;
  sampleCategory: string[];
  coolerId: string;
  noOfSamples: string;
  remark: string;
  /** Report Requirements. */
  tat: string;
  dueDate: string;
  reportTemplate: string;
  eddTemplate: string;
  eddFormat: string;
  reportDeliveryAddress: string;
  reportDeliveryMethod: string;
}

/** A blank header for a New registration. */
export function emptyHeader(registrationType: string, now: string, user: string): SrHeader {
  return {
    jobId: '', receivedDate: now, receivedBy: user, createdBy: user,
    client: '', clientOid: '', clientAddress: '', clientPhone: '', clientContact: '',
    quoteId: '', poNumber: '', paymentStatus: '', manifestNumber: '0',
    projectManager: '', dateExpect: '', relinquishedBy: [], collector: [],
    dateCollected: '', timeCollected: '', projectId: '', projectName: '',
    projectLocation: '', permit: '', projectCategory: '', cocId: '',
    invoiceContact: '', registrationType, jobInactivation: '',
    cancelJob: false, prevStatusId: '',
    status: 'Pending Saving',
    matrices: [], testTemplate: [], testTemplateMethods: {},
    sampleCategory: [], coolerId: '', noOfSamples: '1', remark: '',
    tat: '', dueDate: '', reportTemplate: '', eddTemplate: '', eddFormat: '',
    reportDeliveryAddress: '', reportDeliveryMethod: '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lookup catalogs (prototype reference data — see the file header)
// ─────────────────────────────────────────────────────────────────────────────

/** One grid-picker lookup: column captions + rows (column 0 is the DISPLAY
 *  value shown in the closed field). `oidByLabel` maps the display value to
 *  the FK Oid the API needs at Save time — populated from Col5 of the SP
 *  payload. Absent (empty) for text/enum lookups (Report Template, EDD
 *  Export Format) where the display value IS what gets persisted. */
export interface SrLookup {
  cols: string[];
  rows: string[][];
  oidByLabel?: Record<string, string>;
}

/** Resolve a display value (as stored in SrHeader) to the FK Oid the API
 *  expects. Returns null when the lookup has no Oid map (text/enum) or the
 *  label was never picked from a lookup row. Callers should use `?? null`
 *  to send NULL for optional FKs. */
export function resolveLookupOid(lookup: SrLookup, label: string): string | null {
  if (!label) return null;
  return lookup.oidByLabel?.[label] ?? null;
}

/** Every lookup key the Registration form opens a grid picker for. */
export type SrLookupKey =
  | 'client' | 'project' | 'contact' | 'quote' | 'matrix' | 'test' | 'tat'
  | 'payment' | 'pm' | 'category' | 'sampleCategory' | 'permit' | 'cocid' | 'inact' | 'edd'
  | 'rpt' | 'eddtpl' | 'employee' | 'relinq' | 'collector';

export const LOOKUPS: Record<SrLookupKey, SrLookup> = {
  // Column metadata stays local; rows are loaded from
  // GET /api/v1/sample-registration/lookups.
  client: { cols: ['Client', 'Address', 'Phone'], rows: [] },
  project: { cols: ['Project ID', 'Project Name', 'Location'], rows: [] },
  contact: { cols: ['Contact', 'Email'], rows: [] },
  quote: { cols: ['Quote ID', 'Title', 'Status'], rows: [] },
  matrix: { cols: ['Sample Matrix'], rows: [] },
  test: { cols: ['Test', 'Method', 'Group'], rows: [] },
  tat: { cols: ['TAT'], rows: [] },
  payment: { cols: ['Payment Status'], rows: [] },
  pm: { cols: ['Project Manager'], rows: [] },
  category: { cols: ['Project Category'], rows: [] },
  // "Sample Category" is a SEPARATE aggregate — dbo.SampleCategory
  // (SampleCategoryName), distinct from Project Category which lives in
  // dbo.ProjectCategory.  The legacy Samplecheckin.SampleCategory column
  // stores a ';'-joined list of these Oids (multi-select).
  sampleCategory: { cols: ['Sample Category'], rows: [] },
  permit: { cols: ['Permit'], rows: [] },
  cocid: { cols: ['COC ID'], rows: [] },
  inact: { cols: ['Action', 'Inactivation Reason'], rows: [] },
  // EDD Export Format is NOT an FK — it's the enum `EDDFormat` on the legacy
  // Samplecheckin BO, persisted as an INT in Samplecheckin.EDDFormat. The rows
  // here are the display labels; EDD_FORMAT_TO_INT maps them to the enum value.
  edd: { cols: ['EDD Export Format'], rows: [
    ['Export to Excel All Sheet'], ['Export to Excel Single Sheet'],
    ['Export to CSV'], ['Export to TXT'], ['Export to XML'],
  ] },
  rpt: { cols: ['Report Template'], rows: [] },
  eddtpl: { cols: ['EDD Template'], rows: [] },
  // "Received By" — fed by the GLOBAL employee lookup (GET /api/v1/lookups/employees
  // → dbo.AI_Employee_Lookup_SP), NOT by the Sample Registration lookups SP.
  // ONE column: dbo.Employee.DisplayName verbatim, already sorted ASC by the SP.
  employee: { cols: ['Employee'], rows: [] },
  relinq: { cols: ['Relinquished By'], rows: [] },
  collector: { cols: ['Collector'], rows: [] },
};

/** Lookups that support the inline "＋ New {record}" quick-add (legacy parity:
 *  "Select a client from the dropdown; Allow to add a new one"). */
export const NEWABLE: Partial<Record<SrLookupKey, string>> = {
  client: 'Client', contact: 'Contact', project: 'Project',
  category: 'Project Category', pm: 'Project Manager', payment: 'Payment Status',
  inact: 'Job Inactivation', permit: 'Permit', cocid: 'COC ID',
  relinq: 'Relinquished By', collector: 'Collector',
};

/** CheckedComboBox lookups — tick several, popup stays open (legacy parity). */
export const MULTI_LOOKUPS: SrLookupKey[] = ['matrix', 'test', 'relinq', 'collector', 'sampleCategory'];

/**
 * EDD Export Format — the legacy `EDDFormat` enum on Samplecheckin.cs
 * (persisted as INT in dbo.Samplecheckin.EDDFormat). Order/values are the enum's
 * implicit ints, verified against the BO:
 *   NA=0 · ExporttoExcelAllSheet=1 · ExportToExcelSingleSheet=2 ·
 *   ExporttoCSV=3 · ExporttoTXT=4 · ExporttoXML=5
 * The picker stores the DISPLAY label; these maps convert display ↔ enum int
 * for save (toInt) and round-trip load (fromInt).
 */
export const EDD_FORMAT_TO_INT: Record<string, number> = {
  'Export to Excel All Sheet': 1,
  'Export to Excel Single Sheet': 2,
  'Export to CSV': 3,
  'Export to TXT': 4,
  'Export to XML': 5,
};
export const EDD_INT_TO_FORMAT: Record<number, string> = {
  1: 'Export to Excel All Sheet',
  2: 'Export to Excel Single Sheet',
  3: 'Export to CSV',
  4: 'Export to TXT',
  5: 'Export to XML',
};

/** Display label → EDDFormat enum int (null for N/A / unknown). */
export function eddFormatToInt(label: string | undefined): number | null {
  if (!label) return null;
  return EDD_FORMAT_TO_INT[label] ?? null;
}
/** EDDFormat enum int (or its string form from the API) → display label. */
export function eddFormatFromInt(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(n) ? String(value) : (EDD_INT_TO_FORMAT[n] ?? '');
}

/** TAT → turnaround in DAYS.  Due Date = Received Date + TAT (legacy rule;
 *  the API must own this once the save endpoint lands). */
export const TAT_DAYS: Record<string, number> = {
  '1 Day': 1, '3 Days': 3, '5 Days': 5, '10 Days': 10,
};

/** Test-assignment catalog — [Test, Method, Component, IsGroup, TestGroup]. */
export const TA_CATALOG: readonly (readonly [string, string, string, string, string])[] = [
  ['Colilert', 'SM 9223B', 'Default', 'No', ''],
  ['Total Coliform', 'SM 9223B', 'Default', 'No', ''],
  ['E. coli', 'SM 9223B', 'Default', 'No', ''],
  ['Free Ammonia', 'Field Method', 'Default', 'No', ''],
  ['Free Residual Chlorine', 'Field Method', 'Default', 'No', ''],
  ['Monochloramine', 'Field Method', 'Default', 'No', ''],
  ['Total Residual Chlorine', 'Field Method', 'Default', 'No', ''],
  ['Metals (36)', 'EPA 200.8', 'Default', 'Yes', ''],
  ['Nitrate', 'EPA 300.0', 'Default', 'No', ''],
  ['PFAS EPA 1633', 'EPA 1633', 'Default', 'No', 'PFAS 1633'],
  ['PFAS EPA 1633', 'EPA 1633', 'Watershed', 'No', 'PFAS 1633'],
  ['PFAS_533', 'EPA 533', 'Default', 'No', ''],
  ['PFAS_533', 'EPA 533', 'Watershed', 'No', ''],
  ['Turbidity', 'EPA 180.1', 'Default', 'No', ''],
  ['pH', 'SM 4500-H+', 'Default', 'No', ''],
];

const PFAS_PARAMS = [
  '11Cl-PF3OUdS', '4:2 FTS', '6:2 FTS', '8:2 FTS', '9Cl-PF3ONS', 'ADONA', 'HFPO-DA',
  'NFDHA', 'PFBA', 'PFBS', 'PFDA', 'PFDoA', 'PFDS', 'PFEESA', 'PFHpA', 'PFHpS',
  'PFHxA', 'PFHxS', 'PFMBA', 'PFMPA', 'PFNA', 'PFOA', 'PFOS', 'PFPeA', 'PFUnA',
];

/** Test → its analyte (parameter) list.  A test not listed here has exactly one
 *  analyte with the same name. */
export const PARAMS: Record<string, string[]> = {
  'Metals (36)': ['Arsenic', 'Barium', 'Cadmium', 'Chromium', 'Copper', 'Iron', 'Lead',
    'Manganese', 'Mercury', 'Nickel', 'Selenium', 'Silver', 'Zinc'],
  'PFAS_533': PFAS_PARAMS,
  'PFAS EPA 1633': PFAS_PARAMS,
  'Total Coliform': ['Total Coliform'],
  'E. coli': ['E. coli'],
  'Colilert': ['Total Coliform', 'E. coli'],
  'Nitrate': ['Nitrate (as N)'],
  'Free Ammonia': ['Free Ammonia (as N)'],
  'Free Residual Chlorine': ['Free Residual Chlorine'],
  'Monochloramine': ['Monochloramine'],
  'Total Residual Chlorine': ['Total Residual Chlorine'],
  'Turbidity': ['Turbidity'],
  'pH': ['pH'],
};

/** Every analyte for a test (single-analyte tests fall back to their own name). */
export function paramsOf(testName: string): string[] {
  return PARAMS[testName] ?? [testName];
}

/** Per-test default container + preservative, pre-filled in the bottle picker. */
const TEST_DEFAULTS: Record<string, { container: string; preserv: string }> = {
  'Adjusted Gross Alpha': { container: '1 L Cube, Plastic', preserv: 'HNO3' },
  'Total Coliform': { container: '250 mL Sterile, Plastic', preserv: 'Na2S2O3' },
  'Colilert': { container: '250 mL Sterile, Plastic', preserv: 'Na2S2O3' },
  'E. coli': { container: '250 mL Sterile, Plastic', preserv: 'Na2S2O3' },
  'Metals (36)': { container: '500 mL, Plastic', preserv: 'HNO3' },
  'Nitrate': { container: '250 mL, Plastic', preserv: 'H2SO4' },
  'PFAS_533': { container: '250 mL HDPE', preserv: 'Trizma' },
  'PFAS EPA 1633': { container: '250 mL HDPE', preserv: 'None' },
  'Free Ammonia': { container: '250 mL, Plastic', preserv: 'H2SO4' },
  'Free Residual Chlorine': { container: 'Field', preserv: 'None' },
  'Monochloramine': { container: 'Field', preserv: 'None' },
  'Total Residual Chlorine': { container: 'Field', preserv: 'None' },
  'Turbidity': { container: '100 mL, Plastic', preserv: 'None' },
  'pH': { container: 'Field', preserv: 'None' },
};

export function testDefaults(name: string): { container: string; preserv: string } {
  return TEST_DEFAULTS[name] ?? { container: '250 mL, Plastic', preserv: 'None' };
}

/** Storage locations (from Sample Location) → their storage condition.
 *  @deprecated Replaced by BottleLookups.storages from the API; kept as fallback. */
export const STORAGE_OPTS: readonly (readonly [string, string])[] = [
  ['', ''],
  ['FR-01', 'Refrigerated 2–8 °C'], ['FR-02', 'Refrigerated 2–8 °C'],
  ['FZ-01', 'Frozen −20 °C'], ['FZ-02', 'Frozen −80 °C'],
  ['AMB-01', 'Ambient 15–25 °C'], ['AMB-02', 'Ambient 15–25 °C'],
  ['ARCH', 'Archive'],
];

export function storageCondition(id: string): string {
  return STORAGE_OPTS.find(o => o[0] === id)?.[1] ?? '';
}

// ── Bottle / Container reference lookups (DB-driven) ─────────────────────

export interface BottleLookupOption { oid: string; name: string }

export interface BottleLookups {
  containers: BottleLookupOption[];
  preservatives: BottleLookupOption[];
  storages: BottleLookupOption[];
  storageConditions: BottleLookupOption[];
}

/** Sample / test status dropdown (legacy ALPACA list; '' = not set). */
export const SAMPLE_STATUS_OPTS = [
  '', 'Instrument Down', 'Rejected', 'Out of service', 'Rejected/Recollection',
  'Sub Out', 'Not on the COCs', 'Hold',
];

/** Method → holding time (legacy rule, shown read-only on Test Assignment). */
function holdTime(name: string, method: string): string {
  if (name.startsWith('PFAS')) return '27d 22hr 29m';
  if (method === 'SM 9223B') return '30 hr';
  if (method === 'Field Method') return '15 min';
  if (method === 'EPA 300.0') return '48 hr';
  if (method === 'EPA 200.8') return '6 mo';
  return '';
}

/**
 * A test is a FIELD method when its method is the "Field Method" designation.
 * ONLY field methods let the Sample Login user key results here; lab methods get
 * their results in Result Entry, so their result cells stay read-only.
 */
export function isFieldTest(t: SrTest): boolean {
  return t.method === 'Field Method';
}

/** The Sample Login grid's default column set — [key, type, width].
 *  type: '' = text · number / date / time pickers · 'ro' = read-only ·
 *  'qty' = bottle count · 'status' = the sample-status dropdown.
 *  Legacy makes this configurable in Settings; this is the live default set. */
export const SL_COLS: readonly (readonly [string, string, number])[] = [
  ['SampleName', '', 130], ['SiteName', '', 120], ['SamplingAddress', '', 160],
  ['Qty', 'qty', 70], ['AlternativeStation', '', 130], ['DateCollected', 'date', 135],
  ['TimeCollected', 'time', 115], ['Collector', '', 110], ['ReceivedDate', 'ro', 130],
  ['SampleStatus', 'status', 130], ['SampleMatrix', 'ro', 110], ['CollectorPhone', '', 120],
  ['PWSID', '', 90], ['PWSSystemName', '', 130], ['ProjectNumber', '', 110], ['KeyMap', '', 90],
  ['SampleType', '', 100], ['WaterSource', '', 110], ['FreeColorine(mg/L)', 'number', 130],
  ['TotalChlorine(mg/L)', 'number', 130], ['RejectionCriteria', '', 125], ['RepeatLocation', '', 120],
  ['SamplingEvent', '', 110], ['EnteredBy', 'ro', 95], ['EnteredDate', 'ro', 105],
  ['Address', '', 130], ['SysSampleCode', '', 120], ['ParentSampleID', 'ro', 120],
  ['Remark', '', 120], ['Description', '', 130], ['PlannedLatitude', 'number', 120],
  ['PlannedLongitude', 'number', 125], ['QCCategory', '', 100],
];

/** Sample Condition Check points — verbatim from the live Check Points Setup,
 *  including the "coolor" spelling (preserve-the-quirk convention, §5.2). */
export const SCC_POINTS: readonly (readonly [string, string])[] = [
  ['Cooler seal present and signed', 'no'],
  ['Sample(s) in a cooler', 'yes'],
  ['If yes, ice in the coolor', 'yes'],
  ['Sample(s) received with chain-of-custody', 'yes'],
  ['C-O-C signed and dated', 'yes'],
  ['Sample(s) received with signed sample custody seal', 'no'],
  ['Sample containers arrived intact (if no comment)', 'yes'],
  ['Sample(s) were received in appropriate container with preservation for the required method(s)', 'yes'],
  ['All samples were logged and labeled', 'yes'],
  ['Sample ID labels match C-O-C IDs', 'yes'],
  ['Bottle count on C-O-C matches bottle found', 'yes'],
  ['Sample volume is sufficient for analysis requested', 'yes'],
  ['Samples were received within the holding time', 'yes'],
  ['VOA vials completely filled, no air in the head space', 'na'],
  ['Sample(s) accepted', 'yes'],
  ['pH of sample checked and as required by method', 'na'],
  ['Free chlorine of sample checked and <0.1mg/L', 'yes'],
];

/** Item Charge pricing option from dbo.ItemChargePricing. */
export interface IcPricingOption {
  oid: string;
  itemCode: string;
  itemName: string;
  unitPrice: number;
  npUnitPrice: number;
}

/** Entry Guidance — verbatim from the user manual's field table; surfaced as a
 *  hover tooltip on every General / Sample / Report label. */
export const GUIDANCE: Record<string, string> = {
  'Job ID': 'Autogenerated identifier, read only',
  'Received Date': 'Showing current datetime automatically, editable',
  'Received By': 'Login user, editable',
  'Created By': 'Login user, read only',
  'Client': 'Select a client from the dropdown; Allow to add a new one',
  'Client Address': 'Auto displaying after a client is selected',
  'Client Phone': 'Auto displaying if the phone number has been added to the client',
  'Client Contact': 'Select a contact from the dropdown',
  'Quote ID': 'Select a Quote ID from the dropdown, optional',
  'PO #': 'Enter a PO number, optional',
  'Payment Status': 'Select a payment from the dropdown',
  'Relinquished By': 'Select one or add a new one',
  'Date Collected': 'Enter collection date',
  'Time Collected': 'Enter collection time, optional',
  'Collector': 'Select a collector from the dropdown box',
  'Manifest Number': 'Enter a number, optional',
  'Project ID': 'Select one from the dropdown box',
  'Project Name': 'Auto displaying if it is linked to the selected Project ID, read only',
  'Project Location': 'Auto displaying if it is linked to the selected Project ID, read only',
  'Project Category': 'Select one from the dropdown box, optional',
  'Status': 'Showing the current progress, read only',
  'Sample Matrix': 'Select one or multiple matrices from the dropdown box',
  'Test': 'Select one or more tests under the current sample matrix',
  'No Of Samples': 'Enter number of samples',
  'Sample Category': 'Select a category from the dropdown box, optional',
  'Remark': 'Enter comment if needed',
  'TAT': 'Select a TAT from the dropdown box',
  'Due Date': 'The due date will be automatically displayed',
  'Report Template': 'Select a report template, optional',
  'Report Delivery Address': 'Enter the special report address, optional',
  'Report Delivery Method': 'Enter the report delivery method, optional',
};

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────────────

let keySeq = 0;
/** Client-side stable key for a grid row (never persisted). */
export function nextKey(prefix: string): string {
  keySeq += 1;
  return `${prefix}-${keySeq}`;
}

/** Build a fresh test assignment from the catalog. */
export function mkTest(name: string, method: string, comp?: string, added = '', testGroupName = ''): SrTest {
  const d = testDefaults(name);
  const cat = TA_CATALOG.find(r => r[0] === name && (!comp || r[2] === comp))
    ?? TA_CATALOG.find(r => r[0] === name);
  return {
    key: nextKey('t'),
    name, method, comp: comp || cat?.[2] || 'Default',
    testGroupName,
    bottle: 'A',
    container: d.container, preserv: d.preserv, storage: '',
    bd: null, params: null, paramData: {},
    hdTime: holdTime(name, method),
    status: '', remark: false, subOut: false, added, hold: false,
  };
}

/**
 * Structural copy that KEEPS the test's key — for immutable state updates.
 * Row identity must survive an edit, or every grid row would remount and the
 * current-record (▶) marker and any open picker would lose their target.
 */
export function copyTest(t: SrTest): SrTest {
  return {
    ...t,
    params: t.params ? { ...t.params } : null,
    paramData: Object.fromEntries(Object.entries(t.paramData).map(([k, v]) => [k, { ...v }])),
    bd: t.bd ? Object.fromEntries(Object.entries(t.bd).map(([k, v]) => [k, { ...v }])) : null,
  };
}

/** Copy as a NEW record (fresh key) — for genuine duplication: Copy Samples,
 *  Copy Tests to Samples, and seeding a new sample from the test template. */
export function cloneTest(t: SrTest): SrTest {
  return { ...copyTest(t), key: nextKey('t') };
}

/**
 * Bottle grouping (legacy rule): tests sharing the same container + preservative
 * go in ONE bottle (e.g. Total Coliform + E. coli).  Qty = the number of
 * DISTINCT bottles — Qty 1 with 5 bottles is not a valid state.
 * Mutates and returns the sample.
 */
export function autoBottles(s: SrSample): SrSample {
  const letters = 'ABCDEFGHIJKLMNOP';
  const map = new Map<string, string>();
  for (const t of s.tests) {
    const key = `${t.container}|${t.preserv}`;
    if (!map.has(key)) map.set(key, letters[map.size] ?? `A${map.size}`);
    t.bottle = map.get(key)!;
  }
  s.v.Qty = String(Math.max(1, map.size));
  return s;
}

/** Qty follows the distinct bottle count after a MANUAL bottle change. */
export function syncQty(s: SrSample): void {
  const set = new Set<string>();
  for (const t of s.tests) {
    for (const b of (t.bottle || 'A').split(',')) {
      const x = b.trim();
      if (x) set.add(x);
    }
  }
  s.v.Qty = String(Math.max(1, set.size));
}

/**
 * "Same bottle = one physical container": every test in a bottle shares one
 * Storage / Container / Preservative (first non-empty value wins), so the
 * Containers view and Test Assignment can never disagree.
 */
export function normBottle(s: SrSample): void {
  const rep = new Map<string, Partial<Pick<SrTest, 'storage' | 'container' | 'preserv'>>>();
  const fields = ['storage', 'container', 'preserv'] as const;
  for (const t of s.tests) {
    const b = t.bottle || '';
    const cur = rep.get(b) ?? {};
    for (const f of fields) if (!cur[f] && t[f]) cur[f] = t[f];
    rep.set(b, cur);
  }
  for (const t of s.tests) {
    const cur = rep.get(t.bottle || '');
    if (!cur) continue;
    for (const f of fields) { const v = cur[f]; if (v) t[f] = v; }
  }
}

/** How many analytes a test currently includes, and its picker label. */
export function paramLabel(t: SrTest): string {
  const total = paramsOf(t.name).length;
  const n = t.params ? Object.values(t.params).filter(Boolean).length : total;
  return n === total ? 'AllParam' : `Custom (${n})`;
}

/** Analysis-charge rollup — qty per test = how many samples carry it. */
export interface SrChargeRow { test: string; method: string; qty: number; unitPrice: number; amount: number }

export function chargeRows(samples: SrSample[]): { rows: SrChargeRow[]; total: number } {
  const agg = new Map<string, { method: string; qty: number }>();
  for (const s of samples) {
    for (const t of s.tests) {
      const cur = agg.get(t.name) ?? { method: t.method, qty: 0 };
      cur.qty += 1;
      agg.set(t.name, cur);
    }
  }
  const rows = [...agg.entries()].map(([test, { method, qty }]) => {
    const priceCell = LOOKUPS.test.rows.find(r => r[0] === test)?.[2] ?? '$0.00';
    const unitPrice = Number.parseFloat(priceCell.replace('$', '')) || 0;
    return { test, method, qty, unitPrice, amount: unitPrice * qty };
  });
  return { rows, total: rows.reduce((a, r) => a + r.amount, 0) };
}

/** MM/DD/YYYY (or an ISO string) → yyyy-MM-dd for a native date input. */
export function toDateInput(v: string): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const parts = v.split(' ')[0]?.split('/') ?? [];
  const [m, d, y] = parts;
  return m && d && y ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : '';
}

/** yyyy-MM-ddTHH:mm for a native datetime-local input. */
export function toDateTimeInput(v: string): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 16);
  const [datePart, timePart] = v.split(' ');
  const iso = toDateInput(datePart ?? '');
  return iso ? `${iso}T${(timePart ?? '00:00').slice(0, 5)}` : '';
}

/** Add N calendar days to a yyyy-MM-dd / datetime-local value, returning yyyy-MM-dd. */
export function addDays(value: string, days: number): string {
  const iso = toDateInput(value);
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(y, m - 1, d + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

// ── Working-day / holiday-skipping due-date calc (mirrors SampleRegistrationRules.cs) ──

function isWorkingDay(dt: Date, holidays: ReadonlySet<string>): boolean {
  const dow = dt.getDay();
  if (dow === 0 || dow === 6) return false;
  const pad = (n: number) => String(n).padStart(2, '0');
  const key = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  return !holidays.has(key);
}

function addWorkingDays(dt: Date, daysToAdd: number, holidays: ReadonlySet<string>): Date {
  const r = new Date(dt.getTime());
  let remaining = daysToAdd;
  while (remaining > 0) {
    r.setDate(r.getDate() + 1);
    if (isWorkingDay(r, holidays)) remaining--;
  }
  return r;
}

function addWorkingHours(dt: Date, hoursToAdd: number, holidays: ReadonlySet<string>): Date {
  const r = new Date(dt.getTime());
  let remaining = hoursToAdd;
  while (remaining > 0) {
    r.setHours(r.getHours() + 1);
    if (isWorkingDay(r, holidays)) remaining--;
  }
  return r;
}

/** Resolve due date from received date + TAT hours, skipping weekends/holidays.
 *  Returns a datetime-local string (yyyy-MM-ddTHH:mm), or '' if not computable.
 *  Mirrors SampleRegistrationRules.ResolveDueDate — hours >= 24 uses working
 *  days, < 24 uses working hours. */
export function resolveDueDate(
  receivedDate: string,
  tatHours: number,
  isSampling: boolean,
  holidays: ReadonlySet<string>,
): string {
  if (isSampling) return '';
  if (!receivedDate || tatHours <= 0) return '';
  const dtl = toDateTimeInput(receivedDate);
  if (!dtl) return '';
  const parts = dtl.split('T');
  const dp = parts[0] ?? '';
  const tp = parts[1] ?? '00:00';
  const dParts = dp.split('-').map(Number);
  const tParts = tp.split(':').map(Number);
  const y = dParts[0] ?? 0, mo = dParts[1] ?? 0, da = dParts[2] ?? 0;
  const hh = tParts[0] ?? 0, mm = tParts[1] ?? 0;
  if (!y || !mo || !da) return '';
  const from = new Date(y, mo - 1, da, hh, mm);
  if (isNaN(from.getTime())) return '';

  const result = tatHours >= 24
    ? addWorkingDays(from, Math.floor(tatHours / 24), holidays)
    : addWorkingHours(from, tatHours, holidays);

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${result.getFullYear()}-${pad(result.getMonth() + 1)}-${pad(result.getDate())}T${pad(result.getHours())}:${pad(result.getMinutes())}`;
}

/** {JobID}-NNN sample id. */
export function sampleId(jobId: string, ordinal: number): string {
  return `${jobId || 'NEW'}-${String(ordinal).padStart(3, '0')}`;
}

/** Re-number every sample id so they stay sequential and contiguous. */
export function renumber(samples: SrSample[], jobId: string): void {
  samples.forEach((s, i) => { s.id = sampleId(jobId, i + 1); });
}
